// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsSims from '../../../../api/actionsSims';
// Components
import OrganizationsTable from '@/app/modules/organizations/components/tables/OrganizationsTable';
import SimCardsTable from '../../../tables/SimCardsTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import SelectSimSelectionMode from '../components/SelectSimSelectionMode';
import AddSimCardsByProvider from '../../addSimCard/components/AddSimCardsByProvider';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import splitLines from '../../../../funcs/splitLines';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
import useSimsStore from '@/app/store/modules/sims';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
/**
 *
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 * @returns {JSX} CustomPayModalReassignSimCards
 */
function CustomPayModalReassignSimCards({ isOpen, setIsOpen, setLaunchRefetch }) {

    // Toast
    const { toast } = useToast();

    // Constants
    const STEPS_NUMBER = 4;
    const STEP_TITLE_PREFIX = 'modal.reassign.steps';

    // Store
    const storeQuerySimCardsFromModal = useSimsStore(s => s.storeQuerySimCardsFromModal);

    // State
    const [step, setStep] = useState(0);
    const [simCardsSelected, setSimCardsSelected] = useState(getInitialValueForRowSelected());
    const [simNumberList, setSimNumberList] = useState('');
    const [clientSelected, setClientSelected] = useState(getInitialValueForRowSelected());
    const [actionSelected, setActionSelected] = useState('');
    const [isInvalidSimInput, setIsInvalidSimInput] = useState(false);

    // API Calls Assign SimCard
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity={'modal.reassign.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.reassign.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitReassignSimCards = actionsSims.reassignAndUnassignSimCards(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.action_selection`,
                disabledNext: !actionSelected
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.sims_selection`,
                disabledNext: (actionSelected === 'select' && simCardsSelected.rowsData.length === 0)
                    || (actionSelected === 'input' && isInvalidSimInput)
            },
            2: {
                stepTitle: `${STEP_TITLE_PREFIX}.client_selection`,
                disabledNext: !clientSelected.rowsData.length
            },
            3: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirmation`,
                disabledNext: false
            }
        }),
        [simCardsSelected, actionSelected, simNumberList, isInvalidSimInput, clientSelected]
    );

    useEffect(() => {
        storeQuerySimCardsFromModal(getInitialQuery());
    }, []);

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={actionSelected === 'select' && step === 1 || step === 2 ? 'max-w-6xl' : 'max-w-lg'}>
                <HeaderTitle title={'modal.reassign.title'} namespace={nsSimCard} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {
                        step === 0 &&
                        <div>
                            <Entity entity={'modal.reassign.labels.action'} namespace={nsSimCard} />
                            <SelectSimSelectionMode actionSelected={actionSelected} setActionSelected={setActionSelected} />
                        </div>
                    }
                    {
                        step === 1 &&
                        <>
                            {
                                actionSelected === 'select' ?
                                    <SimCardsTable
                                        filtersEventName={setFiltersEventName('sim_cards', true)}
                                        rowsSelected={simCardsSelected}
                                        setRowsSelected={setSimCardsSelected}
                                        isFromModal={true}
                                    />
                                    :
                                    <AddSimCardsByProvider
                                        simNumberList={simNumberList}
                                        setSimNumberList={setSimNumberList}
                                        setDisableSubmit={setIsInvalidSimInput}
                                    />
                            }
                        </>
                    }
                    {
                        step === 2 &&
                        <OrganizationsTable
                            filtersEventName={setFiltersEventName('organizations', true)}
                            singleSelection={true}
                            rowsSelected={clientSelected}
                            setRowsSelected={setClientSelected}
                        />
                    }
                    {
                        step === 3 &&
                        <Entity entity={'modal.reassign.text.confirmation'} namespace={nsSimCard} data={{ clientName: clientSelected.rowsData[0].legalName }} />
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    stepsNumber={STEPS_NUMBER}
                    setStep={setStep}
                    onValidate={() => {
                        submitReassignSimCards.mutate({
                            simsList: actionSelected === 'select' ? simCardsSelected.rowsData.map(elem => elem.sim.iccid) : splitLines(simNumberList),
                            reassignToCustomer: clientSelected.rowsData[0].uuid
                        });
                    }}
                    disableValidation={disabledNext}
                    isLoading={submitReassignSimCards.isLoading}
                />
            </ModalContent>
        </Modal>
    );
}

CustomPayModalReassignSimCards.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default CustomPayModalReassignSimCards;